/**
 * Utilities for conditional logic expressions in FlowGate.
 * Shared by ConditionBuilder, VariablePicker, ScreenPanel, and generateFlowJson.
 */

export const SIMPLE_OPERATORS = [
  { value: 'eq',  label: 'equals',          expr: '==' },
  { value: 'ne',  label: 'does not equal',  expr: '!=' },
  { value: 'gt',  label: 'is greater than', expr: '>'  },
  { value: 'lt',  label: 'is less than',    expr: '<'  },
  { value: 'gte', label: 'is at least',     expr: '>=' },
  { value: 'lte', label: 'is at most',      expr: '<=' },
];

/**
 * Component types whose named fields can be used as condition sources.
 */
export const CONDITIONABLE_TYPES = new Set([
  'TextInput',
  'TextArea',
  'DatePicker',
  'CalendarPicker',
  'Dropdown',
  'RadioButtonsGroup',
  'CheckboxGroup',
  'ChipsSelector',
  'OptIn',
]);

/**
 * Returns the semantic type of a component for value-input rendering.
 * 'choice'  → show a dropdown of data-source options
 * 'boolean' → show a true/false dropdown (OptIn)
 * 'date'    → text input with date hint
 * 'text'    → plain text input
 */
function getFieldType(componentType) {
  if (['Dropdown', 'RadioButtonsGroup', 'CheckboxGroup', 'ChipsSelector'].includes(componentType)) {
    return 'choice';
  }
  if (componentType === 'OptIn') return 'boolean';
  if (['DatePicker', 'CalendarPicker'].includes(componentType)) return 'date';
  return 'text';
}

/**
 * Returns conditionable fields from a list of components on one screen.
 * Each entry: { fieldName, label, type, dataSource }
 */
export function getConditionableFields(screenComponents) {
  if (!Array.isArray(screenComponents)) return [];
  return screenComponents
    .filter(comp => CONDITIONABLE_TYPES.has(comp.type) && comp.properties?.name)
    .map(comp => ({
      fieldName: comp.properties.name,
      label: comp.properties.label || comp.properties.name,
      type: getFieldType(comp.type),
      // Only static option arrays feed the condition value picker. A dynamic
      // binding is a `${data.x}` STRING, passing it through would crash
      // ValueInput's `.map`; null degrades it to a free-text input.
      dataSource: Array.isArray(comp.properties['data-source']) ? comp.properties['data-source'] : null,
    }));
}

/**
 * Returns all named fields across screens that precede currentScreenId,
 * grouped for the VariablePicker.
 *
 * Returns: [ { screenId, screenTitle, isCurrent, fields: [...] }, ... ]
 */
export function getAllFieldsAcrossScreens(screens, currentScreenId) {
  const currentIndex = screens.findIndex(s => s.id === currentScreenId);
  return screens
    .slice(0, currentIndex + 1)
    .map((screen, idx) => {
      const isCurrent = screen.id === currentScreenId;
      return {
        screenId: screen.id,
        screenTitle: screen.title || `Screen ${idx + 1}`,
        isCurrent,
        fields: getConditionableFields(screen.components),
      };
    })
    .filter(group => group.fields.length > 0);
}

/**
 * Try to parse an inner expression (no backticks) back to simple-mode fields.
 * Returns { mode, field, operator, value, expression } or null if not a simple pattern.
 */
export function parseSimpleExpression(expr) {
  if (!expr) return null;
  // Matches: ${form.fieldName} op 'value'   (op may be == != >= <= > <)
  const match = expr.match(/^\$\{form\.([a-zA-Z_][a-zA-Z0-9_]*)\} (==|!=|>=|<=|>|<) '([^']*)'$/);
  if (!match) return null;
  const [, fieldName, opExpr, value] = match;
  const op = SIMPLE_OPERATORS.find(o => o.expr === opExpr);
  if (!op) return null;
  return { mode: 'simple', field: fieldName, operator: op.value, value, expression: expr };
}

/**
 * Build the inner expression string (without backticks) from simple-mode values.
 * Returns null if inputs are incomplete.
 */
export function buildSimpleExpression(fieldName, operator, value) {
  const op = SIMPLE_OPERATORS.find(o => o.value === operator);
  if (!op || !fieldName) return null;
  const escapedValue = `'${value}'`;
  return `\${form.${fieldName}} ${op.expr} ${escapedValue}`;
}

// condition objects use `field`; routing rule objects use `fieldName`
function resolveExpression(source, withBackticks) {
  if (!source) return null;
  let expr;
  if (source.mode === 'power') {
    expr = source.expression?.trim() || null;
  } else {
    const field = source.field ?? source.fieldName;
    expr = buildSimpleExpression(field, source.operator, source.value);
  }
  if (!expr) return null;
  return withBackticks ? `\`${expr}\`` : expr;
}

/** Bare If.condition string (v7.3 format, no backticks). */
export const conditionToIfExpression = condition => resolveExpression(condition, false);

/** Backtick-wrapped visible expression for WhatsApp reactive visibility. */
export const conditionToVisibleExpression = condition => resolveExpression(condition, true);

/** Bare If.condition string for routing rules (v7.3 format, no backticks). */
export const ruleToIfExpression = rule => resolveExpression(rule, false);

/** Backtick-wrapped If condition expression for routing rules. */
export const ruleToConditionExpression = rule => resolveExpression(rule, true);

/**
 * Build the variable reference string for a field.
 * isCurrent=true  → "${form.fieldName}"
 * isCurrent=false → "${screen.SCREEN_ID.form.fieldName}"
 */
export function buildVariableReference(screenId, fieldName, isCurrent) {
  if (isCurrent) return `\${form.${fieldName}}`;
  return `\${screen.${screenId}.form.${fieldName}}`;
}
